/**
 * Traduccion GLOBAL de los errores del backend a mensajes para personas.
 *
 * El Worker responde `{ error: '<codigo>' }` (y, en `gate_failed`, un `gate` con las actividades que bloquean).
 * `ApiError` (backend.ts) usa `friendlyMessage` como su `message`, asi que TODA pantalla que ya muestre
 * `error.message` queda traducida sin tocar cada `catch`; el codigo crudo sigue disponible en `error.code`.
 *
 * Cubre los 51 codigos que el backend puede devolver (verificado con grep sobre apps/workers-backend/src).
 * Un codigo desconocido cae a un mensaje por estado HTTP: nunca se le muestra al usuario `snake_case`.
 */
import { ACTIVITY_STATUS_LABEL, type ActivityStatus } from '@rat/shared';

/** Detalle que devuelve POST /units/:id/status cuando la compuerta bloquea (ver unit-gates.ts en el backend). */
export interface GateInfo {
  reason: 'no_activities' | 'blocked_by_activities';
  by_status: Record<string, number>;
  sample: Array<{ id: string; ref_code: string | null; title: string; status: string }>;
}

const MESSAGES: Record<string, string> = {
  // ── sesion y acceso ──
  unauthorized: 'Su sesión expiró o no es válida. Vuelva a iniciar sesión.',
  invalid_credentials: 'Correo o contraseña incorrectos.',
  missing_credentials: 'Ingrese su correo y su contraseña.',
  too_many_attempts: 'Demasiados intentos. Espere unos minutos antes de volver a intentarlo.',
  invalid_code: 'El código no es correcto. Ingrese el que muestra ahora su aplicación de autenticación.',
  code_expired:
    'Ese código ya venció. Ingrese el que muestra ahora su aplicación. Si le pasa seguido, active la hora automática en su teléfono.',
  malformed_code: 'El código tiene 6 dígitos y solo números.',
  missing_code: 'Ingrese el código de verificación.',
  not_started: 'Primero debe iniciar la inscripción del segundo factor.',
  already_enrolled: 'Esta cuenta ya tiene configurado el segundo factor. Ingrese el código de su aplicación de autenticación al iniciar sesión.',
  forbidden: 'Su rol no permite realizar esta acción.',
  not_found: 'El recurso no existe o no está dentro de su alcance.',

  // ── flujo de la actividad ──
  invalid_transition: 'Ese cambio de estado no está permitido desde el estado actual.',
  conflict_of_interest:
    'Por separación de funciones, no puede decidir sobre una actividad que usted envió o que pertenece a su propia unidad. Debe revisarla otra persona.',
  assigned_to_other: 'Otra persona ya tomó esta revisión.',
  already_assigned: 'Esta revisión ya fue tomada por otra persona.',
  assignee_not_eligible: 'La persona elegida no puede revisar esta unidad con su alcance actual.',
  closed: 'La tarea de revisión ya está cerrada.',
  unresolved_obligatory_observations:
    'No se puede aprobar: quedan observaciones obligatorias sin resolver. El revisor debe confirmar cada una.',
  observations_pending_response:
    'Antes de marcar la actividad como corregida, responda cada observación obligatoria en la ficha de la actividad.',
  sensitive_without_reinforced_basis:
    'La actividad trata datos sensibles y no declara una base de licitud reforzada. Agréguela antes de enviarla a revisión.',
  concurrent_change: 'Otra persona modificó este registro al mismo tiempo. Recargue la página e inténtelo de nuevo.',

  // ── observaciones ──
  frozen: 'La actividad ya está aprobada o cerrada: sus observaciones no admiten cambios.',
  invalid_state: 'Esa acción no corresponde al estado actual de la observación.',
  not_in_review: 'Solo se pueden agregar observaciones a una actividad que está en revisión.',

  // ── estado de la unidad ──
  gate_failed: 'No se puede cambiar el estado de la unidad todavía.',

  // ── administracion de usuarios, roles y catalogos ──
  duplicate_email: 'Ya existe un usuario con ese correo.',
  weak_password: 'La contraseña debe tener al menos 8 caracteres.',
  last_admin: 'No se puede quitar al último administrador del sistema.',
  unknown_user: 'El usuario indicado no existe.',
  unknown_scope: 'La unidad o categoría indicada no existe.',
  duplicate: 'Esa asignación ya existe.',
  invalid: 'La combinación de rol y alcance no es válida.',
  invalid_catalog: 'El catálogo indicado no existe.',
  duplicate_code: 'Ya existe un ítem con ese código en el catálogo.',

  // ── importacion y adjuntos ──
  too_large: 'El archivo supera el tamaño máximo permitido (20 MB).',
  empty_file: 'El archivo está vacío.',
  unsupported_type: 'Ese tipo de archivo no está permitido. Suba PDF, Word, Excel, CSV, texto o una imagen (PNG/JPG/WEBP).',
  too_many_rows: 'El archivo tiene demasiadas filas (máximo 500 por carga).',
  invalid_kind: 'El tipo de importación no es válido.',
  invalid_form: 'El formulario de carga no es válido.',
  invalid_version: 'Las versiones a comparar no son válidas.',
  missing_file: 'Seleccione un archivo.',

  // ── datos faltantes o mal formados ──
  invalid_body: 'Los datos enviados no son válidos.',
  invalid_json: 'Los datos enviados no son válidos.',
  missing_fields: 'Faltan datos obligatorios.',
  missing_status: 'Falta indicar el estado.',
  missing_decision: 'Falta indicar la decisión.',
  missing_title: 'Falta el título.',
  missing_unit: 'Falta indicar la unidad.',
  missing_items: 'Falta indicar los elementos.',
  missing_rows: 'El archivo no trae filas para importar.',
  missing_full_name: 'Falta el nombre completo.',
};

/** Etiqueta legible de un estado de actividad (o el mismo valor si no es uno conocido). */
const statusLabel = (s: string): string => ACTIVITY_STATUS_LABEL[s as ActivityStatus] ?? s;

/** Explica QUE actividades bloquean el cambio de estado de la unidad. */
export function describeGate(gate: GateInfo): string {
  if (gate.reason === 'no_activities') {
    return 'La unidad todavía no tiene actividades registradas. Registre al menos una antes de continuar.';
  }
  const entries = Object.entries(gate.by_status).filter(([, n]) => n > 0);
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const parts = entries.map(([status, n]) => `${statusLabel(status)}: ${n}`).join(', ');
  const examples = gate.sample
    .slice(0, 3)
    .map((a) => (a.ref_code ? `${a.ref_code} «${a.title}»` : `«${a.title}»`))
    .join(', ');
  return (
    `No se puede cambiar el estado de la unidad: ${total === 1 ? 'hay 1 actividad que lo impide' : `hay ${total} actividades que lo impiden`}` +
    (parts ? ` (${parts})` : '') +
    (examples ? `. Por ejemplo: ${examples}` : '') +
    '.'
  );
}

/** Mensaje de respaldo cuando el codigo no esta en el diccionario: por estado HTTP, nunca el codigo crudo. */
function byStatus(status: number): string {
  if (status === 401) return MESSAGES.unauthorized!;
  if (status === 403) return MESSAGES.forbidden!;
  if (status === 404) return MESSAGES.not_found!;
  if (status === 409) return 'La acción entra en conflicto con el estado actual del registro. Recargue la página e inténtelo de nuevo.';
  if (status === 429) return MESSAGES.too_many_attempts!;
  if (status >= 500) return 'El servidor tuvo un problema. Inténtelo de nuevo en unos minutos.';
  return 'No se pudo completar la operación.';
}

/** El mensaje que ve la persona para un codigo del backend (con el detalle de la compuerta si lo hay). */
export function friendlyMessage(code: string, gate?: GateInfo, status?: number): string {
  if (code === 'gate_failed' && gate) return describeGate(gate);
  return MESSAGES[code] ?? byStatus(status ?? 0);
}

/**
 * Para los `catch` que reciben `unknown`: un ApiError ya trae su mensaje amigable; un fallo de red
 * (fetch lanza TypeError) tambien se traduce.
 */
export function errorMessage(e: unknown, fallback = 'No se pudo completar la operación.'): string {
  if (e instanceof TypeError) return 'No se pudo conectar con el servidor. Revise su conexión e inténtelo de nuevo.';
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

/** Los codigos que el diccionario cubre (para las pruebas). */
export const KNOWN_ERROR_CODES: readonly string[] = Object.keys(MESSAGES);
